import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Info, Lightbulb } from "lucide-react"
import type { IssuerScopeProposal } from "../../../../../api/types"
import { useNumberFormat } from "../../../../home/useNumberFormat"
import type { BrowserView } from "./ArticleBrowser"
import { hasIssuerMatch } from "./suggestions"

/** The article browser's view for the issuer proposal: `ArticleBrowserSheet`
 *  puts that suggestion first whenever there is one. */
export const ISSUER_SUGGESTION_VIEW: BrowserView = {
  kind: "suggestion",
  index: 0,
}

/** Whether, WITHOUT a match, the pool carries several manufacturers (TUR-1720):
 *  the whole-pool reading then deserves a word before the reviewer decides. */
export function needsSeveralManufacturersHint(
  proposal: IssuerScopeProposal | null | undefined,
): boolean {
  return !!proposal && !hasIssuerMatch(proposal) && proposal.multiManufacturer
}

/** Whether `IssuerScopeCardNote` renders anything: a caller wrapping it in a
 *  layout line asks first, so no flag-off card grows an empty line. */
export function hasIssuerScopeNote(
  proposal: IssuerScopeProposal | null | undefined,
): boolean {
  return hasIssuerMatch(proposal) || needsSeveralManufacturersHint(proposal)
}

/**
 * MC_ISSUER_SCOPE_PROPOSAL (TUR-1720), no match: this supplier carries several
 * manufacturers and none of them is the issuer (or, when the issuer matches
 * several of them equally well, none can be picked), so a statement for "all
 * articles" may claim more than the letter covers. Says so, asserts nothing:
 * the reviewer decides in the article browser.
 */
export function SeveralManufacturersHint({
  proposal,
}: {
  proposal: IssuerScopeProposal
}) {
  const { translate } = useTranslation()
  const { format } = useNumberFormat()
  const lead = translate(
    proposal.groupField === "product_group"
      ? "MC_DECL_V2_ISSUER_NOTE_SEVERAL_GROUPS"
      : "MC_DECL_V2_ISSUER_NOTE_SEVERAL_MANUFACTURERS",
    { count: format(proposal.groupCount) },
  )
  return (
    <div className="sv2-artnote" data-testid="issuer-scope-hint">
      <Info className="sv2-ic" />
      <span>
        {lead}{" "}
        {proposal.issuerName &&
          `${translate(
            proposal.issuerAmbiguous
              ? "MC_DECL_V2_ISSUER_NOTE_AMBIGUOUS"
              : "MC_DECL_V2_ISSUER_NOTE_NO_MATCH",
            { issuer: proposal.issuerName },
          )} `}
        {translate("MC_DECL_V2_ISSUER_NOTE_CHECK", {
          total: format(proposal.poolTotal),
        })}
      </span>
    </div>
  )
}

/**
 * The statement card's line for the proposal (scope_all cards only, see
 * `ArticleRow`): with a match, the supplier-scoped count and a button that
 * opens the article browser ON the proposal, where the reviewer stages and
 * applies it; without one, the several-manufacturers hint. Never applies
 * anything itself. A collapsed card shows it too (`UnitCard`): its sentence
 * alone reads as all articles covered, which is what this line qualifies.
 */
export function IssuerScopeCardNote({
  proposal,
  onOpenArticleBrowser,
}: {
  proposal: IssuerScopeProposal | null | undefined
  onOpenArticleBrowser?: (view: BrowserView) => void
}) {
  const { translate } = useTranslation()
  const { format } = useNumberFormat()
  if (!proposal) return null
  if (needsSeveralManufacturersHint(proposal))
    return <SeveralManufacturersHint proposal={proposal} />
  if (!hasIssuerMatch(proposal)) return null
  const text = (
    <>
      <Lightbulb className="sv2-ic" />
      <span>
        {translate("MC_DECL_V2_ISSUER_NOTE_MATCH", {
          value: proposal.matchedValue ?? "",
          n: format(proposal.matchedCount),
          total: format(proposal.poolTotal),
        })}
        {onOpenArticleBrowser && (
          <>
            {" "}
            <b>{translate("MC_DECL_V2_ISSUER_NOTE_OPEN")}</b>
          </>
        )}
      </span>
    </>
  )
  // The same quiet note as a button, like a reader hint that lands its row
  // (`ReaderHintNotes`): opening the browser is the whole gesture.
  return onOpenArticleBrowser ? (
    <button
      type="button"
      className="sv2-artnote sv2-artnote-link"
      data-testid="issuer-scope-proposal"
      onClick={() => onOpenArticleBrowser(ISSUER_SUGGESTION_VIEW)}
    >
      {text}
    </button>
  ) : (
    <div className="sv2-artnote" data-testid="issuer-scope-proposal">
      {text}
    </div>
  )
}
